/**
 * Telegram Mini App (TMA) авто-логин гостя.
 *
 * Когда приложение открыто как Mini App из бота отеля, Telegram кладёт в
 * `window.Telegram.WebApp.initData` подписанную строку с данными пользователя.
 * Мы обмениваем её на гостевую сессию (POST /v1/guest-auth/tg-webapp): бэк
 * проверяет подпись токеном бота ИМЕННО этого тенанта (per-tenant) и ищет
 * активный грант, к которому привязан этот TG-юзер.
 *
 * Поток (вызывается из bootstrap ДО рендера React):
 *   - вне Telegram (initData пуст) → no-op, обычный поток (логин/анон);
 *   - 200 authorized → пишем токены, App стартует уже авторизованным гостем;
 *   - not_bound → уводим на /guest-login (self-auth: фамилия+номер+даты);
 *   - ошибка сети/подписи → no-op (покажем обычный логин, не зацикливаемся).
 */
import { guestTgWebApp, staffTgWebApp } from '@hsc/api-client';
import { setTenantSlugProvider } from '@hsc/api-client';
import { resolveTenantSlug } from './tenant-slug.js';
import { writeLocalAccessToken, writeLocalRefreshToken } from './storage.js';

interface SafeAreaInset {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
}

interface TelegramWebApp {
  initData?: string;
  ready?: () => void;
  expand?: () => void;
  // Bot API 8.0 — отступы безопасной зоны (px). В вебвью Telegram системный
  // env(safe-area-inset-bottom) обычно = 0, поэтому нижнюю панель приходится
  // поднимать вручную по этим значениям.
  safeAreaInset?: SafeAreaInset;
  contentSafeAreaInset?: SafeAreaInset;
  onEvent?: (event: string, cb: () => void) => void;
}

function telegramWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  const tg = (window as unknown as { Telegram?: { WebApp?: TelegramWebApp } })
    .Telegram;
  return tg?.WebApp ?? null;
}

/** true, когда страница открыта внутри Telegram (есть непустой initData). */
export function isTelegramMiniApp(): boolean {
  const wa = telegramWebApp();
  return Boolean(wa?.initData && wa.initData.length > 0);
}

/** Минимальный запас под нижнюю «ручку»/панель Telegram, когда инсеты = 0. */
const TMA_FALLBACK_SAFE_BOTTOM_PX = 20;

/**
 * Помечает <html> классом `tma` и выставляет CSS-переменную
 * `--tg-safe-bottom` из contentSafeAreaInset/safeAreaInset (Bot API 8.0).
 * В вебвью Telegram env(safe-area-inset-bottom) обычно 0 → нижняя нав-панель
 * прячется за системной зоной; CSS читает `--tg-safe-bottom` как минимум (#78).
 * Идемпотентно; переустанавливает значение на событиях изменения инсетов.
 */
export function applyTelegramSafeArea(): void {
  if (typeof document === 'undefined') return;
  const wa = telegramWebApp();
  if (!wa) return;

  const root = document.documentElement;
  root.classList.add('tma');

  const sync = (): void => {
    const bottom =
      wa.contentSafeAreaInset?.bottom ??
      wa.safeAreaInset?.bottom ??
      0;
    const px = Math.max(bottom, TMA_FALLBACK_SAFE_BOTTOM_PX);
    root.style.setProperty('--tg-safe-bottom', `${px}px`);
  };

  sync();
  try {
    wa.onEvent?.('safeAreaChanged', sync);
    wa.onEvent?.('contentSafeAreaChanged', sync);
  } catch {
    /* старый клиент без событий инсетов — статичного запаса достаточно */
  }
}

/** Результат авто-логина — используется bootstrap для роутинга/лоадера. */
export type TmaBootstrapOutcome = 'authorized' | 'not_bound' | 'skip';

/**
 * Пытается авто-залогинить гостя по Telegram initData. Никогда не бросает —
 * любая ошибка деградирует в 'skip' (обычный поток логина).
 */
export async function tryTelegramAutoLogin(): Promise<TmaBootstrapOutcome> {
  const wa = telegramWebApp();
  const initData = wa?.initData;
  if (!wa || !initData) return 'skip';

  // Сообщаем Telegram, что мини-апп готов, и разворачиваем на весь экран.
  try {
    wa.ready?.();
    wa.expand?.();
  } catch {
    /* SDK может отсутствовать в эмуляции — не критично */
  }

  // api-client'у нужен X-Tenant-Slug ещё до монтирования ApiBridge.
  setTenantSlugProvider(() => resolveTenantSlug());

  try {
    // 1) Сначала пробуем вход ПЕРСОНАЛА (#88): если этот Telegram привязан к
    //    staff-аккаунту, входим с реальными ролями сотрудника (admin/manager/…),
    //    а НЕ как гость. not_bound → падаем в гостевой поток ниже.
    try {
      const staff = await staffTgWebApp(initData);
      if (staff.outcome === 'authorized') {
        writeLocalAccessToken(staff.access_token);
        if (staff.refresh_token) writeLocalRefreshToken(staff.refresh_token);
        return 'authorized';
      }
    } catch {
      /* staff-эндпойнт недоступен/ошибка → пробуем гостевой ниже */
    }

    // 2) Гостевой вход по привязке (как раньше).
    const res = await guestTgWebApp(initData);
    if (res.outcome === 'authorized') {
      writeLocalAccessToken(res.access_token);
      if (res.refresh_token) writeLocalRefreshToken(res.refresh_token);
      return 'authorized';
    }
    return 'not_bound';
  } catch {
    // Плохая подпись / сеть / сервис недоступен → обычный логин.
    return 'skip';
  }
}
